import { Controller } from "@hotwired/stimulus"
import { useStore } from "stimulus-store"
import { areaStore } from "../stores/locales/area_store"
import { deviceTypeFilterOptionsStore } from "../stores/testing_task_status/device_type_filter_options_store"
import { deploymentStatusFilterOptionsStore } from "../stores/testing_task_status/deployment_status_filter_options_store"
import { deviceTypeFilterSelectionStore } from "../stores/testing_task_status/device_type_filter_selection_store"
import { deploymentStatusFilterSelectionStore } from "../stores/testing_task_status/deployment_status_filter_selection_store"
import { sendFailureNotification } from "shared/toast_notifications"
import { getDevicesInLocale } from "shared/devices/device_discovery"
import { getCsrfToken } from "shared/form_helpers"

export default class extends Controller {
    // The option/selection stores MUST be declared before areaStore. useStore
    // initializes stores (and their setters) in declaration order, and areaStore
    // fires onAreaStoreUpdate — which writes the filter stores — so those setters
    // must already exist by the time areaStore's callback can run.
    static stores = [
        deviceTypeFilterOptionsStore,
        deploymentStatusFilterOptionsStore,
        deviceTypeFilterSelectionStore,
        deploymentStatusFilterSelectionStore,
        areaStore
    ]
    static targets = [
        'cardContainer',
        'loadingIndicator',
        'emptyState',
        'errorNotification',
        'controlsBar',
        'sortByNameBtn',
        'sortByRoomBtn'
    ]
    static values = {
        canUpdate: Boolean
    }

    connect() {
        useStore(this)
        // Reset the page-local filter stores so the widgets start blank/disabled on
        // every Turbo navigation (they persist as module-level singletons otherwise).
        this.setDeviceTypeFilterOptionsStoreValue([])
        this.setDeploymentStatusFilterOptionsStoreValue([])
        this.setDeviceTypeFilterSelectionStoreValue('')
        this.setDeploymentStatusFilterSelectionStoreValue('')
        this._devices = []
        this._taskDefinitions = {}
        this._deviceTypeNames = {}
        this._changeListenerAttached = false
        this._sortMode = 'name'
        // Monotonic token used to discard stale/overlapping area loads. areaStore is
        // a module-level singleton that persists across Turbo navigations and can
        // notify more than once (e.g. a stale value on connect followed by the real
        // user selection). Without this guard, an earlier load can resolve AFTER a
        // later one and clobber freshly-rendered cards with stale, eventually-
        // consistent data — silently discarding an optimistic status change the user
        // just made. Only the newest load is allowed to mutate state / re-render.
        this._areaLoadToken = 0
        this._currentAreaId = null
        this._hasFullAreaData = false
        this.clearCards()
    }

    disconnect() {
        this._devices = []
        this._taskDefinitions = {}
        this._deviceTypeNames = {}
        this._currentAreaId = null
    }

    // Store callbacks

    onAreaStoreUpdate(area) {
        if (!area || !area.id) {
            // Area cleared: remove cards and show empty state
            this._currentAreaId = null
            this._hasFullAreaData = false
            this._clearDeviceTypeFilter()
            this.clearCards()
            this.hideLoading()
            this.showEmptyState()
            return
        }

        // The area store writes a partial {id, name} object synchronously first,
        // then enriches it with region/building asynchronously once
        // GET /api/locales/areas/describe resolves (see area_select_controller.js's
        // getArea().then(...)). Deduping on id alone drops that enrichment, since by
        // the time it arrives _currentAreaId already equals its id -- the page then
        // queries /api/items/list with area only (no region/building), which never
        // matches the item's project_locale composite key and silently returns zero
        // devices. Only skip when this really is a no-op: same area AND we already
        // have (or this payload still lacks) the region/building needed to query it.
        const hasLocationData = area.region != null && area.building != null
        if (area.id === this._currentAreaId && (this._hasFullAreaData || !hasLocationData)) {
            return
        }

        this._currentAreaId = area.id
        this._hasFullAreaData = hasLocationData

        // Clear device type filter on a genuine area change
        this._clearDeviceTypeFilter()

        // Area selected: fetch devices and task definitions
        this._loadDataForArea(area)
    }

    // Filter selection store callbacks

    onDeviceTypeFilterSelectionStoreUpdate() {
        this.applyFilters()
    }

    onDeploymentStatusFilterSelectionStoreUpdate() {
        this.applyFilters()
    }

    applyFilters() {
        // Keep this guard: the selection-store callbacks above also fire during the
        // connect-time store resets (and the widgets' own setValue('')), which can run
        // while the card container is present-but-empty. In that state this iterates
        // zero cards — an intended, benign no-op.
        if (!this.hasCardContainerTarget) {
            return
        }

        const selectedType = this.deviceTypeFilterSelectionStoreValue || ''
        const selectedStatus = this.deploymentStatusFilterSelectionStoreValue || ''

        const cards = this.cardContainerTarget.querySelectorAll('[data-device-type-id]')
        cards.forEach(card => {
            const matchesType = !selectedType || card.dataset.deviceTypeId === selectedType
            const matchesStatus = !selectedStatus || card.dataset.deploymentStatusId === selectedStatus

            if (matchesType && matchesStatus) {
                card.classList.remove('d-none')
            } else {
                card.classList.add('d-none')
            }
        })
    }

    sortByName() {
        this._sortMode = 'name'
        if (this.hasSortByNameBtnTarget && this.hasSortByRoomBtnTarget) {
            this.sortByNameBtnTarget.classList.add('active')
            this.sortByRoomBtnTarget.classList.remove('active')
        }
        if (this._devices.length > 0) {
            this._rerenderWithFilter()
        }
    }

    sortByRoom() {
        this._sortMode = 'room'
        if (this.hasSortByNameBtnTarget && this.hasSortByRoomBtnTarget) {
            this.sortByRoomBtnTarget.classList.add('active')
            this.sortByNameBtnTarget.classList.remove('active')
        }
        if (this._devices.length > 0) {
            this._rerenderWithFilter()
        }
    }

    // Data fetching

    async _loadDataForArea(area) {
        // Claim a token for this load. Any load that started earlier becomes stale
        // the moment a newer one begins, and stale loads must not mutate state.
        const loadToken = ++this._areaLoadToken

        this.clearCards()
        this.hideEmptyState()
        this.showLoading()

        try {
            // Step 1: Fetch all devices in the area (paginated)
            const devices = await this.fetchDevicesForArea(area)

            // Abort if a newer area load has started while we were fetching.
            if (loadToken !== this._areaLoadToken) {
                return
            }

            if (!devices || devices.length === 0) {
                this.hideLoading()
                this.showEmptyState()
                this._disableDeviceTypeFilter()
                return
            }

            // Step 2: Collect unique device type IDs
            const deviceTypeIds = [...new Set(devices.map(d => d.device_type).filter(Boolean))]

            if (deviceTypeIds.length === 0) {
                this.hideLoading()
                this.showEmptyState()
                this._disableDeviceTypeFilter()
                return
            }

            // Step 3: Fetch testing task definitions for each device type
            const taskDefinitions = await this.fetchTestingTaskDefinitions(deviceTypeIds)

            // Abort again — the definitions fetch is another async hop during which a
            // newer load may have superseded this one.
            if (loadToken !== this._areaLoadToken) {
                return
            }

            // Step 4: Filter devices to only those whose device type has ≥1 testing task definition
            const filteredDevices = devices.filter(device => {
                const defs = taskDefinitions[device.device_type]
                return defs && defs.length > 0
            })

            // Step 5: Store results
            this._devices = filteredDevices
            this._taskDefinitions = taskDefinitions
            this._deviceTypeNames = this._buildDeviceTypeNames(filteredDevices)

            this.hideLoading()

            // Step 6: Show empty state or render cards
            if (filteredDevices.length === 0) {
                this.showEmptyState()
                this._disableDeviceTypeFilter()
            } else {
                this.hideEmptyState()
                this.renderDeviceCards()
            }
        } catch (error) {
            // Ignore errors from superseded loads.
            if (loadToken !== this._areaLoadToken) {
                return
            }

            console.error('Error loading testing task status data:', error)
            this.hideLoading()
            this.showError('Unable to load devices. Please try again.')

            // INTENTIONAL DELTA (improvement, not a non-regression): also clear the
            // filter option stores so a failed reload disables the filters rather than
            // leaving stale options that match zero rendered cards. Today's catch did
            // not touch the filters.
            this.setDeviceTypeFilterOptionsStoreValue([])
            this.setDeploymentStatusFilterOptionsStoreValue([])
        }
    }

    async fetchDevicesForArea(area) {
        // Pull the selected area's region and building IDs out of the area object and
        // let the shared helper handle the paginated GET /api/items/list fetch. This page
        // scopes by area within the active project, so roomId and projectScopeToken are null.
        const items = await getDevicesInLocale('devices', area.region, area.building, area.id, null, null)

        // Normalize items to flatten nested device type info and include task_status
        return items
            .filter(item => item.device) // Only include devices (not parts)
            .map(item => ({
                ...item,
                device_type: item.device?.device_type?.id || null,
                device_type_name: item.device?.device_type?.name || null
            }))
    }

    async fetchTestingTaskDefinitions(deviceTypeIds) {
        const definitions = {}

        for (const deviceTypeId of deviceTypeIds) {
            try {
                const url = `/api/testing_tasks/list?device_type=${ encodeURIComponent(deviceTypeId) }`

                const response = await fetch(url, {
                    method: 'GET',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json'
                    },
                    credentials: 'same-origin'
                })

                if (!response.ok) {
                    throw new Error(`Failed to fetch testing tasks for device type ${ deviceTypeId }: HTTP ${ response.status }`)
                }

                const data = await response.json()
                const tasks = data.testing_tasks || []
                definitions[deviceTypeId] = tasks
            } catch (error) {
                console.error(`Error fetching testing tasks for device type ${ deviceTypeId }:`, error)
                throw error
            }
        }

        return definitions
    }

    // UI state methods

    clearCards() {
        if (this.hasCardContainerTarget) {
            this.cardContainerTarget.innerHTML = ''
        }
    }

    showLoading() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.remove('d-none')
        }
    }

    hideLoading() {
        if (this.hasLoadingIndicatorTarget) {
            this.loadingIndicatorTarget.classList.add('d-none')
        }
    }

    showEmptyState() {
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.classList.remove('d-none')
        }
    }

    hideEmptyState() {
        if (this.hasEmptyStateTarget) {
            this.emptyStateTarget.classList.add('d-none')
        }
    }

    showError(message) {
        if (this.hasErrorNotificationTarget) {
            this.errorNotificationTarget.innerHTML = `
                <div class="alert alert-danger alert-dismissible fade show" role="alert">
                    ${ message }
                    <button type="button" class="btn-close" data-bs-dismiss="alert" aria-label="Close"></button>
                </div>
            `
        }
        sendFailureNotification(message)
    }

    renderDeviceCards() {
        if (!this.hasCardContainerTarget) {
            return
        }

        const devices = this._devices
        const taskDefinitions = this._taskDefinitions
        const deviceTypeNames = this._deviceTypeNames

        // Sort devices based on current sort mode
        if (this._sortMode === 'room') {
            devices.sort((a, b) => {
                const roomA = (a.locale?.room?.name || '').toLowerCase()
                const roomB = (b.locale?.room?.name || '').toLowerCase()
                const cmp = roomA.localeCompare(roomB)
                if (cmp !== 0) {
                    return cmp
                }
                return a.name.toLowerCase().localeCompare(b.name.toLowerCase())
            })
        } else {
            devices.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()))
        }

        // Build card HTML
        let cardsHtml = ''
        devices.forEach(device => {
            const deviceTypeName = deviceTypeNames[device.device_type] || device.device_type_name || 'Unknown'
            const roomName = device.locale?.room?.name || ''
            const truncatedName = this._truncateName(device.name)
            const taskDefs = taskDefinitions[device.device_type] || []

            // Parse task_status JSON
            let taskStatus = {}
            try {
                taskStatus = JSON.parse(device.task_status || '{}')
            } catch (e) {
                taskStatus = {}
            }
            const testingTasks = taskStatus.testing_tasks || []

            // Build testing task sections
            let taskSectionsHtml = ''
            taskDefs.forEach(taskDef => {
                // Sort states by order
                const sortedStates = [...(taskDef.states || [])].sort((a, b) => (a.order || 0) - (b.order || 0))

                // Find pre-selected state
                const matchingEntry = testingTasks.find(entry => entry.id === taskDef.id)
                const selectedState = matchingEntry ? matchingEntry.state : null

                // Build radio buttons
                let radiosHtml = ''
                sortedStates.forEach(status => {
                    const radioId = `status_${ device.id }_${ taskDef.id }_${ status.id }`
                    const radioName = `status_${ device.id }_${ taskDef.id }`
                    const checked = selectedState === status.id ? ' checked' : ''
                    const disabled = !this.canUpdateValue ? ' disabled' : ''

                    radiosHtml += `<input type="radio" class="btn-check" name="${ radioName }" id="${ radioId }" value="${ status.id }" data-device-id="${ device.id }" data-task-id="${ taskDef.id }"${ checked }${ disabled }>`
                    radiosHtml += `<label class="btn btn-outline-primary btn-sm" for="${ radioId }">${ this._escapeHtml(status.name) }</label>`
                })

                // Add "Untested" radio button as last option - selected when no status is set
                const untestedRadioId = `status_${ device.id }_${ taskDef.id }_untested`
                const untestedRadioName = `status_${ device.id }_${ taskDef.id }`
                const untestedChecked = selectedState === null ? ' checked' : ''
                const untestedDisabled = !this.canUpdateValue ? ' disabled' : ''

                radiosHtml += `<input type="radio" class="btn-check" name="${ untestedRadioName }" id="${ untestedRadioId }" value="" data-device-id="${ device.id }" data-task-id="${ taskDef.id }"${ untestedChecked }${ untestedDisabled }>`
                radiosHtml += `<label class="btn btn-outline-secondary btn-sm" for="${ untestedRadioId }">Untested</label>`

                taskSectionsHtml += `
                    <div class="mb-2">
                        <small class="fw-bold d-block mb-1">${ this._escapeHtml(taskDef.name) }</small>
                        <div class="btn-group btn-group-sm w-100" role="group" data-device-id="${ device.id }" data-task-id="${ taskDef.id }">
                            ${ radiosHtml }
                        </div>
                    </div>`
            })

            const deploymentStatusId = device.deployment_status?.id || ''
            const deploymentStatusName = device.deployment_status?.name || ''

            cardsHtml += `
                <div class="col-md-4 col-lg-3 mb-3" data-device-type-id="${ device.device_type }" data-device-id="${ device.id }" data-deployment-status-id="${ deploymentStatusId }">
                    <div class="card h-100">
                        <div class="card-body">
                            <h6 class="card-title" title="${ this._escapeHtml(device.name) }">${ this._escapeHtml(truncatedName) }</h6>
                            <span class="badge bg-secondary mb-1">${ this._escapeHtml(deviceTypeName) }</span>
                            ${ roomName ? `<small class="d-block text-muted mb-2">${ this._escapeHtml(roomName) }</small>` : '<div class="mb-2"></div>' }
                            ${ taskSectionsHtml }
                        </div>
                    </div>
                </div>`
        })

        this.cardContainerTarget.innerHTML = cardsHtml

        // Populate filters
        this._populateDeviceTypeFilter()
        this._populateDeploymentStatusFilter()

        // Add change event listener for radio buttons (only once)
        if (!this._changeListenerAttached) {
            this.cardContainerTarget.addEventListener('change', (event) => {
                if (event.target.type === 'radio') {
                    this.handleStatusChange(event)
                }
            })
            this._changeListenerAttached = true
        }
    }

    async handleStatusChange(event) {
        const radio = event.target
        const deviceId = radio.dataset.deviceId
        const taskId = radio.dataset.taskId
        const newState = radio.value

        // Get the radio group containing this radio
        const radioGroup = radio.closest('[role="group"]')
        const deviceCard = radio.closest('[data-device-type-id]')

        // Store the previously selected state for rollback
        // Since the change event fires AFTER the new radio is checked,
        // we need to find the previous state from our stored device data
        const previousState = this._getPreviousState(deviceId, taskId)

        // Disable the radio group while the request is in-flight
        this._disableRadioGroup(radioGroup)

        // Build the full testing_tasks payload from all radio states on this device card
        const testingTasks = this._buildTestingTasksPayload(deviceCard)

        // Send PUT request with 10-second timeout.
        //
        // The request uses the native fetch API, which is invisible to both
        // jQuery.active and Turbo's navigator. Increment a shared pending-request
        // counter so test/wait tooling (and anything else observing async work) can
        // reliably detect that a fetch is in flight and block until it settles.
        // Decrement happens in `finally` so the counter is balanced on success,
        // failure, timeout, and network error alike.
        this._incrementPendingRequests()
        try {
            const abortController = new AbortController()
            const timeoutId = setTimeout(() => abortController.abort(), 10000)

            const response = await fetch(`/api/items/${ deviceId }`, {
                method: 'PUT',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-Token': getCsrfToken()
                },
                credentials: 'same-origin',
                signal: abortController.signal,
                body: JSON.stringify({ testing_tasks: testingTasks })
            })

            clearTimeout(timeoutId)
            const responseBody = await response.clone().json()

            if (response.ok) {
                // Success: re-enable radio group, keep new selection
                // Update internal device state to reflect the change
                this._updateDeviceState(deviceId, taskId, newState)
                this._enableRadioGroup(radioGroup)
            } else {
                // Failure (non-2xx): revert and show error
                this._revertRadioSelection(radioGroup, previousState)
                this._enableRadioGroup(radioGroup)
                sendFailureNotification('Failed to update testing task status. Please try again.')
            }
        } catch (error) {
            // Timeout or network error: revert and show error
            this._revertRadioSelection(radioGroup, previousState)
            this._enableRadioGroup(radioGroup)

            if (error.name === 'AbortError') {
                sendFailureNotification('Request timed out. Please try again.')
            } else {
                sendFailureNotification('Failed to update testing task status. Please try again.')
            }
        } finally {
            this._decrementPendingRequests()
        }
    }

    // Track in-flight fetch requests on a shared window-level counter so that
    // fetch-based async work is observable to the same tooling that watches
    // jQuery.active and Turbo visits. The counter is process-global (not
    // per-controller) so a single idle check covers all fetch activity.
    _incrementPendingRequests() {
        window.__pendingFetchRequests = (window.__pendingFetchRequests || 0) + 1
    }

    _decrementPendingRequests() {
        const next = (window.__pendingFetchRequests || 0) - 1
        window.__pendingFetchRequests = next > 0 ? next : 0
    }

    // Private helpers — status update

    _getPreviousState(deviceId, taskId) {
        const device = this._devices.find(d => d.id === deviceId)
        if (!device) {
            return null
        }

        let taskStatus = {}
        try {
            taskStatus = JSON.parse(device.task_status || '{}')
        } catch (e) {
            taskStatus = {}
        }
        const testingTasks = taskStatus.testing_tasks || []
        const entry = testingTasks.find(t => t.id === taskId)
        return entry ? entry.state : null
    }

    _updateDeviceState(deviceId, taskId, newState) {
        const device = this._devices.find(d => d.id === deviceId)
        if (!device) {
            return
        }

        let taskStatus = {}
        try {
            taskStatus = JSON.parse(device.task_status || '{}')
        } catch (e) {
            taskStatus = {}
        }

        if (!taskStatus.testing_tasks) {
            taskStatus.testing_tasks = []
        }

        if (newState) {
            // Setting a real status
            const existingIndex = taskStatus.testing_tasks.findIndex(t => t.id === taskId)
            if (existingIndex >= 0) {
                taskStatus.testing_tasks[existingIndex].state = newState
            } else {
                taskStatus.testing_tasks.push({ id: taskId, state: newState })
            }
        } else {
            // "Untested" selected - remove this task's entry
            taskStatus.testing_tasks = taskStatus.testing_tasks.filter(t => t.id !== taskId)
        }

        device.task_status = JSON.stringify(taskStatus)
    }

    _buildTestingTasksPayload(deviceCard) {
        const taskEntries = []
        const radioGroups = deviceCard.querySelectorAll('[role="group"][data-task-id]')
        radioGroups.forEach(group => {
            const taskId = group.dataset.taskId
            const checkedRadio = group.querySelector('input[type="radio"]:checked')
            if (checkedRadio && checkedRadio.value) {
                // Only include tasks with a non-empty value (skip "Untested" selections)
                taskEntries.push({ id: taskId, state: checkedRadio.value })
            }
        })
        return taskEntries
    }

    _disableRadioGroup(group) {
        const radios = group.querySelectorAll('input[type="radio"]')
        radios.forEach(radio => {
            radio.disabled = true
        })
    }

    _enableRadioGroup(group) {
        // Only re-enable if the user has update permission
        if (!this.canUpdateValue) {
            return
        }

        const radios = group.querySelectorAll('input[type="radio"]')
        radios.forEach(radio => {
            radio.disabled = false
        })
    }

    _revertRadioSelection(group, previousStateId) {
        const radios = group.querySelectorAll('input[type="radio"]')
        radios.forEach(radio => {
            if (previousStateId === null) {
                // Revert to "Untested" (the radio with empty value)
                radio.checked = (radio.value === '')
            } else {
                radio.checked = (radio.value === previousStateId)
            }
        })
    }

    // Private helpers — rendering

    _truncateName(name) {
        if (!name) {
            return ''
        }
        if (name.length > 50) {
            return name.substring(0, 50) + '\u2026'
        }
        return name
    }

    _escapeHtml(str) {
        if (!str) {
            return ''
        }
        const div = document.createElement('div')
        div.textContent = str
        return div.innerHTML
    }

    _populateDeviceTypeFilter() {
        // Collect unique device types from rendered devices
        const deviceTypes = {}
        this._devices.forEach(device => {
            if (device.device_type && !deviceTypes[device.device_type]) {
                deviceTypes[device.device_type] = this._deviceTypeNames[device.device_type] || device.device_type_name || 'Unknown'
            }
        })

        // Sort device type names alphabetically and write the option store. The widget
        // consumer rebuilds its TomSelect from this update.
        const list = Object.entries(deviceTypes)
            .sort((a, b) => a[1].toLowerCase().localeCompare(b[1].toLowerCase()))
            .map(([id, name]) => ({ id, name }))

        this.setDeviceTypeFilterOptionsStoreValue(list)
    }

    _populateDeploymentStatusFilter() {
        // Collect unique deployment statuses from devices
        const statuses = {}
        this._devices.forEach(device => {
            const statusId = device.deployment_status?.id
            const statusName = device.deployment_status?.name
            if (statusId && statusName && !statuses[statusId]) {
                statuses[statusId] = statusName
            }
        })

        // Sort status names alphabetically and write the option store.
        const list = Object.entries(statuses)
            .sort((a, b) => a[1].toLowerCase().localeCompare(b[1].toLowerCase()))
            .map(([id, name]) => ({ id, name }))

        this.setDeploymentStatusFilterOptionsStoreValue(list)
    }

    _rerenderWithFilter() {
        // Capture the current selections from the selection stores BEFORE re-rendering.
        // renderDeviceCards() repopulates the option stores, which resets each widget's
        // control (and the selection stores) to ''.
        const deviceTypeValue = this.deviceTypeFilterSelectionStoreValue || ''
        const deploymentStatusValue = this.deploymentStatusFilterSelectionStoreValue || ''

        // Re-render cards (this also repopulates the filter option stores)
        this.renderDeviceCards()

        // Reapply the captured filters to the freshly-rendered cards (unchanged mechanism)
        if ((deviceTypeValue || deploymentStatusValue) && this.hasCardContainerTarget) {
            const cards = this.cardContainerTarget.querySelectorAll('[data-device-type-id]')
            cards.forEach(card => {
                const matchesType = !deviceTypeValue || card.dataset.deviceTypeId === deviceTypeValue
                const matchesStatus = !deploymentStatusValue || card.dataset.deploymentStatusId === deploymentStatusValue
                if (!matchesType || !matchesStatus) {
                    card.classList.add('d-none')
                }
            })
        }

        // Re-write each captured selection back into its selection store ONLY if that id
        // still exists among the freshly-rendered devices. The widget silently reflects
        // it onto the control so the dropdown keeps showing the selection across a sort.
        if (deviceTypeValue && this._devices.some(d => d.device_type === deviceTypeValue)) {
            this.setDeviceTypeFilterSelectionStoreValue(deviceTypeValue)
        }
        if (deploymentStatusValue && this._devices.some(d => d.deployment_status?.id === deploymentStatusValue)) {
            this.setDeploymentStatusFilterSelectionStoreValue(deploymentStatusValue)
        }
    }

    _buildDeviceTypeNames(devices) {
        const names = {}
        devices.forEach(device => {
            if (device.device_type && device.device_type_name && !names[device.device_type]) {
                names[device.device_type] = device.device_type_name
            }
        })
        return names
    }

    _clearDeviceTypeFilter() {
        // Empty option stores → widgets disable and show only the placeholder.
        // Empty selection stores → no active filter.
        this.setDeviceTypeFilterOptionsStoreValue([])
        this.setDeploymentStatusFilterOptionsStoreValue([])
        this.setDeviceTypeFilterSelectionStoreValue('')
        this.setDeploymentStatusFilterSelectionStoreValue('')
    }

    _disableDeviceTypeFilter() {
        // Empty option stores make both widgets disable + show their placeholders.
        this.setDeviceTypeFilterOptionsStoreValue([])
        this.setDeploymentStatusFilterOptionsStoreValue([])
    }
}
